/**
 * shadcn/ui Tabs — vendored + tokenized for CertAIn v2 (Phase 1 fan-out foundation).
 *
 * Radix Tabs (Root / List / Trigger / Content). Colors swapped for CertAIn
 * semantic utilities so they resolve inside `[data-theme="v2"]`. The TabsList /
 * TabsTrigger reproduce the canonical `.tabs` / `.tab` pill idiom from
 * design/v2-direction/index.html (lines 500-505): a muted-bg inline pill track,
 * the active trigger lifted to the elevated surface with a small shadow.
 * Guard-clean (no hex / rgb / arbitrary color).
 *
 * Used by the Candidate detail 5-tab bar (Resume · AI Evaluation · Interview
 * Prep · Interview Analysis · Notes) — patterns/DetailPageV2 composes it.
 */
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Canonical .tabs: inline pill track, muted bg, 3px padding, --radius.
      "inline-flex items-center gap-0.5 rounded-[var(--radius,0.5rem)] bg-surface-subtle p-[3px] text-text-secondary",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Canonical .tab / .tab.active: 13px/550 muted, active lifts to elevated
      // surface + shadow-sm. Blue ring on focus (v2 focus discipline).
      "inline-flex items-center justify-center whitespace-nowrap rounded-[calc(var(--radius,0.5rem)-3px)] px-3 py-1.5 text-[13px] font-medium text-text-secondary transition-colors",
      "hover:text-text-primary",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas",
      "disabled:pointer-events-none disabled:opacity-50",
      "data-[state=active]:bg-surface-elevated data-[state=active]:text-text-primary data-[state=active]:shadow-sm",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // Canonical .tab-panel: margin-top 20px above the panel body.
      "mt-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
